(() => {
  const LABEL_PRINTING_YES_PATTERN = /^yes,?\s+add label printing/i;
  const LABEL_APPLICATION_YES_PATTERN = /^yes,?\s+add label application/i;
  const AUTOMATED_PRINTING_CLASS = 'retail-products-label-pack-automated';
  const AUTOMATED_APPLICATION_CLASS = 'retail-products-label-application-automated';
  const SUMMARY_CLASS = 'retail-products-services-summary';
  const SUMMARY_WRAPPER_CLASS = 'retail-products-services-total-wrapper';
  const HIDDEN_CLASS = 'retail-products-hidden';
  const DEFERRED_VALIDATION_CLASS = 'retail-products-validation-deferred';
  const SYNC_DELAYS = [0, 250, 750, 1600];

  let syncTimers = [];
  let isSynchronising = false;
  let isSynchronisingApplication = false;

  const getProductInfo = () => document.querySelector('product-info');

  const getSelectedVariant = (productInfo) => {
    const selectedVariant = productInfo?.querySelector('variant-selects [data-selected-variant]');
    if (!selectedVariant?.textContent) return null;

    try {
      return JSON.parse(selectedVariant.textContent);
    } catch (error) {
      console.warn('Unable to read the selected retail product variant.', error);
      return null;
    }
  };

  const findVariantOption = (productInfo, optionName) =>
    Array.from(productInfo?.querySelectorAll('[data-option-position]') || []).find((wrapper) => {
      const name = wrapper
        .querySelector('.radio__legend__option-name')
        ?.textContent
        ?.trim()
        .toLowerCase();
      return name?.includes(optionName.toLowerCase());
    });

  const getSelectedOptionValue = (wrapper, variant) => {
    const optionIndex = Number(wrapper?.dataset.optionPosition) - 1;
    return variant?.options?.[optionIndex] ||
      wrapper?.querySelector('[data-popout-toggle-text]')?.textContent?.trim() ||
      '';
  };

  const getSelectedProductQuantity = (productInfo, variant = getSelectedVariant(productInfo)) => {
    const quantityWrapper = findVariantOption(productInfo, 'choose your qty') ||
      findVariantOption(productInfo, 'qty');
    const value = getSelectedOptionValue(quantityWrapper, variant);
    const match = value.match(/(?:^|[^\d])(1000|500|200|100|50|25)(?=[^\d]|$)/);
    return match ? Number(match[1]) : null;
  };

  const findEasifyBlock = (productInfo) =>
    productInfo?.querySelector('[id*="__easify_options_"]') ||
    productInfo?.querySelector('.easify-product-options')?.closest('.shopify-app-block');

  const findEasifyOption = (easifyBlock, labelText) =>
    Array.from(easifyBlock?.querySelectorAll('.tpo_option-container') || []).find((container) => {
      const label = container
        .querySelector('.tpo_option-label-container')
        ?.textContent
        ?.trim()
        .toLowerCase();
      return label?.includes(labelText.toLowerCase());
    });

  const getCheckboxes = (option) =>
    Array.from(option?.querySelectorAll('input[type="checkbox"]') || []);

  const getLabelPrintingInputs = (easifyBlock) => {
    const option = findEasifyOption(easifyBlock, 'label printing');
    const inputs = getCheckboxes(option);
    return {
      option,
      yes: inputs.find((input) => LABEL_PRINTING_YES_PATTERN.test(input.value)),
      no: inputs.find((input) => !LABEL_PRINTING_YES_PATTERN.test(input.value)),
    };
  };

  const getLabelApplicationInputs = (easifyBlock) => {
    const option = findEasifyOption(easifyBlock, 'apply your labels');
    const inputs = getCheckboxes(option);
    return {
      option,
      yes: inputs.find((input) => LABEL_APPLICATION_YES_PATTERN.test(input.value)),
      no: inputs.find((input) => !LABEL_APPLICATION_YES_PATTERN.test(input.value)),
    };
  };

  const isPrintingSelected = (easifyBlock) => {
    const { yes, no } = getLabelPrintingInputs(easifyBlock);
    return Boolean(yes?.checked && !no?.checked);
  };

  const isApplicationSelected = (easifyBlock) => {
    const { yes, no } = getLabelApplicationInputs(easifyBlock);
    return Boolean(yes?.checked && !no?.checked);
  };

  const isApplicationAnswered = (easifyBlock) => {
    const { yes, no } = getLabelApplicationInputs(easifyBlock);
    return Boolean(yes?.checked || no?.checked);
  };

  const enforceExclusiveChoice = (target, { yes, no }) => {
    if (target !== yes && target !== no) return;
    if (!target.checked) return;

    const otherInput = target === yes ? no : yes;
    if (otherInput?.checked) otherInput.click();
  };

  const syncLabelOptionOrder = (easifyBlock) => {
    const printingOption = getLabelPrintingInputs(easifyBlock).option;
    const applicationOption = getLabelApplicationInputs(easifyBlock).option;
    const labelsOnFile = findEasifyOption(easifyBlock, 'labels on file');
    const uploadedLabelFile = findEasifyOption(easifyBlock, 'upload your label file');
    if (!printingOption || !applicationOption) return;

    // Keep the customer journey in this order:
    // printing -> application -> label-file choice -> upload field.
    const parent = printingOption.parentElement;
    if (!parent || applicationOption.parentElement !== parent) return;

    if (printingOption.nextElementSibling !== applicationOption) {
      parent.insertBefore(applicationOption, printingOption.nextElementSibling);
    }

    if (labelsOnFile?.parentElement === parent && applicationOption.nextElementSibling !== labelsOnFile) {
      parent.insertBefore(labelsOnFile, applicationOption.nextElementSibling);
    }

    if (uploadedLabelFile?.parentElement === parent && labelsOnFile?.nextElementSibling !== uploadedLabelFile) {
      parent.insertBefore(uploadedLabelFile, labelsOnFile.nextElementSibling);
    }
  };

  const syncLabelApplicationVisibility = (easifyBlock) => {
    const { option, yes, no } = getLabelApplicationInputs(easifyBlock);
    if (!option) return;

    const shouldShow = isPrintingSelected(easifyBlock);
    const wasShown = option.dataset.retailProductsApplicationShown === 'true';

    option.classList.toggle(HIDDEN_CLASS, !shouldShow);
    option.toggleAttribute('aria-hidden', !shouldShow);
    option.dataset.retailProductsApplicationShown = String(shouldShow);

    if (!shouldShow) {
      option.classList.remove(DEFERRED_VALIDATION_CLASS);
      isSynchronisingApplication = true;
      if (yes?.checked) yes.click();
      if (no && !no.checked) no.click();
      isSynchronisingApplication = false;
      return;
    }

    // The hidden state uses the zero-cost answer to keep Easify's required
    // field valid. Clear it when the question appears so the customer makes an
    // intentional application choice.
    if (!wasShown) {
      isSynchronisingApplication = true;
      if (yes?.checked) yes.click();
      if (no?.checked) no.click();
      isSynchronisingApplication = false;
      option.classList.add(DEFERRED_VALIDATION_CLASS);
    }

    if (isApplicationAnswered(easifyBlock)) {
      option.classList.remove(DEFERRED_VALIDATION_CLASS);
    }
  };

  const syncLabelFileVisibility = (easifyBlock) => {
    const applicationOption = getLabelApplicationInputs(easifyBlock).option;
    const labelsOnFile = findEasifyOption(easifyBlock, 'labels on file');
    const uploadedLabelFile = findEasifyOption(easifyBlock, 'upload your label file');
    if (!applicationOption || !labelsOnFile) return;

    const shouldShow = isPrintingSelected(easifyBlock) && isApplicationAnswered(easifyBlock);

    [labelsOnFile, uploadedLabelFile].forEach((option) => {
      option?.classList.toggle(HIDDEN_CLASS, !shouldShow);
      option?.toggleAttribute('aria-hidden', !shouldShow);
    });

    if (!shouldShow) {
      labelsOnFile
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((input) => input.click());
    }
  };

  const normaliseText = (element) => element?.textContent?.replace(/\s+/g, ' ').trim() || '';

  const getSelectionChoices = (option) =>
    Array.from(
      option?.querySelectorAll(
        'button.tpo_option-type-selection:not(.tpo_option-type-selection--empty)'
      ) || []
    );

  const getQuantityPattern = (quantity) => new RegExp(
    `^x?\\s*${quantity}\\s+(?:units?|labels?|label pack)\\b`,
    'i'
  );

  const getLabelPackFromEasify = (easifyBlock, quantity) => {
    const option = findEasifyOption(easifyBlock, 'how many labels');
    const matchingChoice = getSelectionChoices(option).find((choice) =>
      getQuantityPattern(quantity).test(normaliseText(choice))
    );
    if (!matchingChoice) return null;

    const choiceText = normaliseText(matchingChoice);
    const prices = Array.from(choiceText.matchAll(/\$\s*(\d+(?:\.\d{1,2})?)/g)).map(
      (match) => Number(match[1])
    );
    const perLabelValue = prices.length > 1 ? prices[0] : null;
    const totalValue = prices.length ? prices[prices.length - 1] : null;
    if (!Number.isFinite(totalValue)) return null;

    return {
      label: `x${quantity} Label Pack`,
      price: `$${Number.isInteger(totalValue) ? totalValue : totalValue.toFixed(2)}`,
      priceValue: totalValue,
      perLabel: Number.isFinite(perLabelValue)
        ? `$${perLabelValue.toFixed(2)}ea`
        : `$${(totalValue / quantity).toFixed(2)}ea`,
    };
  };

  const getLabelApplicationPackFromEasify = (easifyBlock, quantity) => {
    const option = findEasifyOption(easifyBlock, 'label application pricing');
    const pattern = new RegExp(
      `^label application for\\s+${quantity}\\s+labels?\\b`,
      'i'
    );
    const matchingChoice = getSelectionChoices(option).find((choice) =>
      pattern.test(normaliseText(choice))
    );
    if (!matchingChoice) return null;

    const choiceText = normaliseText(matchingChoice);
    const prices = Array.from(choiceText.matchAll(/\$\s*(\d+(?:\.\d{1,2})?)/g)).map(
      (match) => Number(match[1])
    );
    const totalValue = prices.length ? prices[0] : null;
    if (!Number.isFinite(totalValue)) return null;

    return {
      price: `$${Number.isInteger(totalValue) ? totalValue : totalValue.toFixed(2)}`,
      priceValue: totalValue,
      perLabel: `$${(totalValue / quantity).toFixed(2)}ea`,
    };
  };

  const chooseMatchingSelection = ({ option, inputName, matchPattern, emptyPattern }) => {
    if (!option) return;

    const valueInput = option.querySelector(`input[name="properties[${inputName}]"]`);
    const choices = getSelectionChoices(option);
    const desiredChoice = choices.find((choice) => matchPattern.test(normaliseText(choice)));
    const currentValue = valueInput?.value?.trim() || '';

    if (desiredChoice && !matchPattern.test(currentValue)) {
      desiredChoice.click();
      return;
    }

    if (!desiredChoice && emptyPattern) {
      const emptyChoice = choices.find((choice) => emptyPattern.test(normaliseText(choice)));
      if (emptyChoice && !emptyPattern.test(currentValue)) emptyChoice.click();
    }
  };

  const syncLabelPrintingQuantity = (easifyBlock, quantity) => {
    const option = findEasifyOption(easifyBlock, 'how many labels');
    if (!option) return;

    option.classList.add(AUTOMATED_PRINTING_CLASS);

    if (!isPrintingSelected(easifyBlock)) {
      chooseMatchingSelection({
        option,
        inputName: 'Label Qty',
        matchPattern: /^no label printing\b/i,
      });
      return;
    }

    chooseMatchingSelection({
      option,
      inputName: 'Label Qty',
      matchPattern: getQuantityPattern(quantity),
    });
  };

  const syncLabelApplicationQuantity = (easifyBlock, quantity) => {
    const option = findEasifyOption(easifyBlock, 'label application pricing');
    if (!option) return;

    option.classList.add(AUTOMATED_APPLICATION_CLASS);

    if (!isApplicationSelected(easifyBlock)) {
      chooseMatchingSelection({
        option,
        inputName: 'Label Application Qty',
        matchPattern: /^no label application\b/i,
      });
      return;
    }

    chooseMatchingSelection({
      option,
      inputName: 'Label Application Qty',
      matchPattern: new RegExp(
        `^label application for\\s+${quantity}\\s+labels?\\b`,
        'i'
      ),
    });
  };

  const formatDollars = (value) => `$${Number.isInteger(value) ? value : value.toFixed(2)}`;

  const renderSubtotal = (easifyBlock, quantity) => {
    const totalWrapper = easifyBlock?.querySelector('.tpo_total-additional-price-wrapper');
    if (!totalWrapper) return;

    const labelPack = isPrintingSelected(easifyBlock)
      ? getLabelPackFromEasify(easifyBlock, quantity)
      : null;
    const applicationPack = isApplicationSelected(easifyBlock)
      ? getLabelApplicationPackFromEasify(easifyBlock, quantity)
      : null;

    if (!labelPack && !applicationPack) {
      totalWrapper.classList.remove(SUMMARY_WRAPPER_CLASS);
      totalWrapper.querySelector(`.${SUMMARY_CLASS}`)?.remove();
      return;
    }

    const summary = document.createElement('div');
    summary.className = SUMMARY_CLASS;

    if (labelPack) {
      const row = document.createElement('p');
      row.replaceChildren(
        document.createTextNode(`Label Printing: ${labelPack.label} `),
        Object.assign(document.createElement('span'), {
          className: 'retail-products-services-price',
          textContent: labelPack.price,
        }),
        document.createTextNode(` (${labelPack.perLabel})`)
      );
      summary.appendChild(row);
    }

    if (applicationPack) {
      const row = document.createElement('p');
      row.replaceChildren(
        document.createTextNode(`Label Application: x${quantity} Labels `),
        Object.assign(document.createElement('span'), {
          className: 'retail-products-services-price',
          textContent: applicationPack.price,
        }),
        document.createTextNode(` (${applicationPack.perLabel})`)
      );
      summary.appendChild(row);
    }

    const subtotal = (labelPack?.priceValue || 0) + (applicationPack?.priceValue || 0);
    const subtotalRow = document.createElement('p');
    subtotalRow.className = 'retail-products-services-subtotal';
    subtotalRow.replaceChildren(
      document.createTextNode('Additional options subtotal: '),
      Object.assign(document.createElement('span'), {
        className: 'retail-products-services-price',
        textContent: formatDollars(subtotal),
      })
    );
    summary.appendChild(subtotalRow);

    totalWrapper.classList.add(SUMMARY_WRAPPER_CLASS);
    totalWrapper.classList.remove('tpo_hidden');
    totalWrapper.hidden = false;
    totalWrapper.replaceChildren(summary);
  };

  const synchronise = (productInfo) => {
    const easifyBlock = findEasifyBlock(productInfo);
    const quantity = getSelectedProductQuantity(productInfo);
    if (!easifyBlock || !quantity) return;

    isSynchronising = true;
    syncLabelOptionOrder(easifyBlock);
    syncLabelApplicationVisibility(easifyBlock);
    syncLabelFileVisibility(easifyBlock);
    syncLabelPrintingQuantity(easifyBlock, quantity);
    syncLabelApplicationQuantity(easifyBlock, quantity);
    renderSubtotal(easifyBlock, quantity);
    isSynchronising = false;
  };

  const scheduleSynchronise = (productInfo) => {
    syncTimers.forEach((timer) => window.clearTimeout(timer));
    syncTimers = SYNC_DELAYS.map((delay) =>
      window.setTimeout(() => synchronise(productInfo), delay)
    );
  };

  const observeEasify = (productInfo) => {
    const observer = new MutationObserver(() => {
      if (!findEasifyBlock(productInfo)) return;

      observer.disconnect();
      scheduleSynchronise(productInfo);
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 20000);
  };

  const initialise = () => {
    const productInfo = getProductInfo();
    if (!productInfo) return;

    observeEasify(productInfo);
    scheduleSynchronise(productInfo);

    if (typeof window.subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== productInfo.dataset.section) return;
        scheduleSynchronise(productInfo);
      });
    }

    productInfo.addEventListener('change', (event) => {
      if (isSynchronising) return;
      if (!event.target.closest('.tpo_option-container')) return;

      const easifyBlock = findEasifyBlock(productInfo);
      enforceExclusiveChoice(event.target, getLabelPrintingInputs(easifyBlock));
      enforceExclusiveChoice(event.target, getLabelApplicationInputs(easifyBlock));

      const applicationOption = getLabelApplicationInputs(easifyBlock).option;
      if (
        !isSynchronisingApplication &&
        event.target.closest('.tpo_option-container') === applicationOption
      ) {
        applicationOption.classList.remove(DEFERRED_VALIDATION_CLASS);
      }
      scheduleSynchronise(productInfo);
    });

    productInfo.addEventListener('click', (event) => {
      if (!event.target.closest('.tpo_option-container')) return;
      scheduleSynchronise(productInfo);
    });

    productInfo.addEventListener('click', (event) => {
      if (!event.target.closest('[data-add-to-cart]')) return;

      getLabelApplicationInputs(findEasifyBlock(productInfo))
        .option
        ?.classList.remove(DEFERRED_VALIDATION_CLASS);
    }, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
